import React, { useEffect, useState } from 'react';
import { Card, Form, Table } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../redux/HooksRedux';
import { RootState } from '../../../redux/rootReducer';
import { checkAccess, convertToLabelValue } from 'utils/helpers';
import { modules, SELECTED_LANGUAGE, ViewAllLimit } from 'utils/enums/misc';
import { GROUP_MANAGEMENT } from 'constant/RoutesUrls';

import {
  successNotification,
  errorNotification
} from 'base/notifications/Notifications';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { StandardLoader } from 'base/Loader/Loader';
import { StandardButton } from 'base/StandardButton/StandardButton';
import StandardInput from 'base/StandardInput/StandardInput';
import StandardSelect from 'base/StandardSelect/StandardSelect';

import { t } from 'i18next';
import { GroupManagementSet } from 'utils/validations/ValidationsSet/GroupManagementSet';

import {
  GroupManagementFormI,
  GroupUserI,
  interfaceGroupAdminLevelMappingI,
  interfaceGroupStateMappingI,
  OptionI,
  paramsI,
  SingleAdminLevelI,
  userGroupMappingI
} from '../../../../types/interface';
import UserSelectModal from '../UserSelectModal';
import { UilTrashAlt } from '@iconscout/react-unicons';

import * as GroupManagementAction from '../../../redux/GroupManagement/GroupManagementAction';
import { getAdminLevels } from '../../../redux/AdminLevel/AdminLevelAction';
import * as userAction from '../../../redux/user/userAction';
import { AxiosResponse } from 'axios';
import { getCookie } from 'utils/cookieUtils';

const GroupManagementForm = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const editGroupId = searchParams.get('editGroupId');
  const currentLanguage = getCookie(SELECTED_LANGUAGE);

  const userPermission = useSelector(
    (state: RootState) => state.userProfile.permission
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<GroupUserI[]>([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);

  const [adminLevelOptions, setAdminLevelOptions] = useState<OptionI[]>([]);
  const [federalStateOptions, setFederalStateOptions] = useState<OptionI[]>([]);
  const [formData, setFormData] = useState<GroupManagementFormI>({
    group_slug: 'state_admin_group',
    group_name_en: '',
    group_name_de: '',
    admin_level: [],
    federal_state: [],
    is_active: false,
    is_verified: false
  });

  // option of Admin Lavel MultiSelect
  const fetchAdminLevels = async () => {
    const params: paramsI = { page: 1, limit: ViewAllLimit, keyword: '' };
    const response: AxiosResponse<{ data: SingleAdminLevelI[] }> =
      await dispatch(getAdminLevels(params));

    if (response?.status === 200) {
      const levelKey =
        currentLanguage === 'de' ? 'level_name_de' : 'level_name_en';

      setAdminLevelOptions(
        convertToLabelValue(response?.data?.data, levelKey, 'admin_level_id')
      );
    }
  };

  // Option of Federalstate MultiSelect
  const fetchFederalStates = async () => {
    const response = await dispatch(userAction.getAllFederalState());
    setFederalStateOptions(
      convertToLabelValue(response?.data?.data, 'name', 'state_id')
    );
  };

  const buildPayload = (data: GroupManagementFormI) => ({
    group_slug: data.group_slug,
    group_name_en: data.group_name_en,
    group_name_de: data.group_name_de,
    state_ids: (data.federal_state ?? []).map(Number),
    admin_level_ids: (data.admin_level ?? []).map(Number),
    user_ids: selectedUsers.map(u => u.user_id),
    is_active: data.is_active,
    is_archived: !data.is_verified
  });

  const fetchSingleGroup = async () => {
    if (!checkAccess([modules.ADMIN_LEVEL.edit], userPermission)) {
      navigate(GROUP_MANAGEMENT);
      return;
    }

    setIsLoading(true);
    const response = await dispatch(
      GroupManagementAction.getSingleGroupManagement(editGroupId)
    );

    if (response?.status === 200) {
      const data = response?.data?.data;

      setFormData({
        group_slug: data.group_slug,
        group_name_en: data.group_name_en,
        group_name_de: data.group_name_de,
        admin_level: data.group_admin_level_mapping?.map(
          (level: interfaceGroupAdminLevelMappingI) =>
            level.admin_level_id?.toString()
        ),
        federal_state: data.group_state_mapping?.map(
          (state: interfaceGroupStateMappingI) => state.state_id
        ),
        is_active: data.is_active,
        is_verified: !data.is_archived
      });
      setSelectedUsers(
        (data.user_group_mapping || []).map((mapping: userGroupMappingI) => ({
          user_id: mapping.users.user_id,
          first_name: mapping.users.first_name,
          last_name: mapping.users.last_name,
          email: mapping.users.email
        }))
      );
    }

    setIsLoading(false);
  };

  useEffect(() => {
    fetchAdminLevels();
    fetchFederalStates();
    if (editGroupId) fetchSingleGroup();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowError(true);

    const isInvalid =
      !formData.group_name_en &&
      !formData.group_name_de &&
      formData.admin_level.length === 0 &&
      formData.federal_state.length === 0;

    if (isInvalid) {
      return;
    }

    const payload = buildPayload(formData);
    setIsLoading(true);

    try {
      if (editGroupId) {
        const response = await dispatch(
          GroupManagementAction.updateGroupManagement(editGroupId, payload)
        );

        if (response?.status === 200) {
          successNotification(
            t('group_management.notification.update_success')
          );
          navigate(GROUP_MANAGEMENT);
        } else {
          errorNotification(response?.data?.message);
        }
      } else {
        const response = await dispatch(
          GroupManagementAction.createGroupManagement(payload)
        );

        if (response?.status === 201) {
          successNotification(
            t('group_management.notification.create_success')
          );
          navigate(GROUP_MANAGEMENT);
        } else {
          errorNotification(response?.data?.message);
        }
      }
    } catch (err: any) {
      errorNotification(err?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMultiSelect = (
    value: number[],
    key: 'admin_level' | 'federal_state'
  ) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Card>
      <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
        <Form onSubmit={handleSubmit}>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h3 className="m-0">{t('group_management.form.header')}</h3>

            <div className="d-flex gap-2">
              <StandardButton
                variant="outline-danger"
                buttonText={t('general.common_text.text_15')}
                onClick={() => navigate(GROUP_MANAGEMENT)}
              />
              <StandardButton
                type="submit"
                buttonText={
                  editGroupId
                    ? t('general.common_text.text_18')
                    : t('general.common_text.text_2')
                }
                onClick={handleSubmit}
              />
            </div>
          </Card.Header>

          <Card.Body>
            <div className="row">
              <div className="col-12 col-md-6">
                <StandardInput
                  label={t('group_management.form.text_1')}
                  placeholder={t('group_management.placeholder.text_1')}
                  value={formData.group_name_en}
                  onChange={e =>
                    setFormData({ ...formData, group_name_en: e as string })
                  }
                  showError={showError}
                  type="text"
                  className=""
                  validations={GroupManagementSet.group_name_en}
                />
              </div>

              <div className="col-12 col-md-6">
                <StandardInput
                  label={t('group_management.form.text_2')}
                  placeholder={t('group_management.placeholder.text_2')}
                  value={formData.group_name_de}
                  onChange={e =>
                    setFormData({ ...formData, group_name_de: e as string })
                  }
                  showError={showError}
                  type="text"
                  className=""
                  validations={GroupManagementSet.group_name_de}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-12 col-md-6">
                <StandardSelect
                  formClassName=""
                  id=""
                  label={t('group_management.form.text_3')}
                  placeholder={t('group_management.placeholder.text_3')}
                  options={adminLevelOptions}
                  value={formData.admin_level}
                  onChange={e => handleMultiSelect(e, 'admin_level')}
                  showError={showError}
                  validations={GroupManagementSet.admin_level}
                  isMulti
                />
              </div>
              <div className="col-12 col-md-6">
                <StandardSelect
                  formClassName=""
                  id=""
                  label={t('group_management.form.text_4')}
                  placeholder={t('group_management.placeholder.text_4')}
                  options={federalStateOptions}
                  value={formData.federal_state}
                  onChange={e => handleMultiSelect(e, 'federal_state')}
                  showError={showError}
                  validations={GroupManagementSet.federal_state}
                  isMulti
                />
              </div>
            </div>
          </Card.Body>

          <Card.Footer>
            <StandardButton
              buttonText={t('group_management.form.text_5')}
              onClick={() => setShowAddUserModal(true)}
              className="mt-1"
            />
          </Card.Footer>

          <ConditionalRender condition={selectedUsers.length > 0}>
            <div className="p-4 pt-2">
              <Table
                className="standard-table table-width phoenix-table"
                responsive
                borderless
              >
                <thead>
                  <tr>
                    <th className="ps-0">#</th>
                    <th className="ps-0">{t('general.common_text.text_5')}</th>
                    <th className="ps-0">{t('general.common_text.text_6')}</th>
                    <th className="ps-0">{t('general.common_text.text_4')}</th>
                    <th className="ps-0">{t('general.common_text.text_8')}</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedUsers.map((user, index) => (
                    <tr key={`selected-user-${user.user_id}`}>
                      <td>{index + 1}</td>
                      <td>{user.first_name}</td>
                      <td>{user.last_name}</td>
                      <td>{user.email}</td>
                      <td>
                        <UilTrashAlt
                          className="cursor-pointer text-danger"
                          onClick={() =>
                            setSelectedUsers(prev =>
                              prev.filter(u => u.user_id !== user.user_id)
                            )
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </ConditionalRender>
        </Form>

        <UserSelectModal
          show={showAddUserModal}
          onHide={() => setShowAddUserModal(false)}
          selected={selectedUsers}
          onConfirm={setSelectedUsers}
        />
      </ConditionalRender>
    </Card>
  );
};

export default GroupManagementForm;
